const BASE = '';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

export interface User {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  provider: string;
  onboarding_completed: boolean;
  default_workspace_id?: string;
  created_at: string;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  created_at: string;
}

export interface WorkspaceStats {
  total_requests: number;
  requests_24h: number;
  members: number;
  api_keys: number;
  recent_events: TraceEvent[];
}

export interface ApiKey {
  id: string;
  prefix: string;
  name: string;
  roles: string[];
  tags: string[];
  created_at: string;
  last_used_at?: string;
  revoked: boolean;
}

export interface ApiKeyCreated extends ApiKey {
  key: string;
}

export interface TraceEvent {
  event_id: string;
  event_type: string;
  tool_name: string;
  task_id: string;
  tool_call_id?: string;
  session_id?: string;
  actor_id?: string;
  workspace_id?: string;
  timestamp: string;
  status?: string;
  metadata?: Record<string, unknown>;
}

export interface SessionSummary {
  session_id: string;
  tool_call_count: number;
  first_timestamp: string;
  last_timestamp: string;
  duration_ms: number;
  actor_id: string;
  platform: string;
  tools_used: string[];
  overall_status: string;
  status_counts: { ok: number; blocked: number; error: number; approval: number; redacted: number };
}

export interface SessionsResponse {
  sessions: SessionSummary[];
  total: number;
}

export interface SessionToolCall {
  tool_call_id: string;
  task_id: string;
  tool_name: string;
  timestamp: string;
  duration_ms: number;
  status: string;
  events: TraceEvent[];
}

export interface SessionDetailResponse {
  session_id: string;
  tool_calls: SessionToolCall[];
  total_events: number;
}

export interface TracesResponse {
  traces: TraceEvent[];
  total: number;
}

export interface EventsQuery {
  event_type?: string;
  tool?: string;
  actor?: string;
  status?: string;
  q?: string;
  since?: string;
  sort?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface EventsResponse {
  events: TraceEvent[];
  total: number;
  offset: number;
  limit: number;
}

export interface BudgetInfo {
  type: string;
  id: string;
  limit: number;
  spent: number;
  remaining: number;
}

export interface BudgetConfig {
  task_budget_usd?: number;
  user_daily_budget_usd?: number;
  user_monthly_budget_usd?: number;
  workspace_daily_budget_usd?: number;
  workspace_monthly_budget_usd?: number;
  user_daily_budget_calls?: number;
  user_monthly_budget_calls?: number;
  workspace_daily_budget_calls?: number;
  workspace_monthly_budget_calls?: number;
  max_steps_per_task?: number;
  max_retries_per_call?: number;
  max_wall_clock_ms?: number;
}

export interface SpendingSummary {
  task_total: number;
  user_daily_total: number;
  user_monthly_total: number;
  workspace_daily_total: number;
  workspace_monthly_total: number;
  user_daily_call_total: number;
  user_monthly_call_total: number;
  workspace_daily_call_total: number;
  workspace_monthly_call_total: number;
}

export interface BudgetsResponse {
  workspace_id: string;
  config: BudgetConfig;
  spending: SpendingSummary;
  is_custom?: boolean;
}

export interface RateLimitConfig {
  enabled?: boolean;
  actor_max_per_window?: number;
  workspace_max_per_window?: number;
  window_ms?: number;
}

export interface RateLimitConfigResponse {
  config: RateLimitConfig;
  is_custom: boolean;
}

export interface BudgetConfigResponse {
  config: BudgetConfig;
  is_custom: boolean;
}

export interface Approval {
  approval_id: string;
  tool_call_id: string;
  task_id: string;
  workspace_id: string;
  actor_id: string;
  tool_name: string;
  tool_capability: string;
  args_summary: string;
  scope: string;
  reason: string;
  status: string;
  created_at: string;
  expires_at: string;
}

export interface TraceDetailEvent {
  event_id: string;
  event_type: string;
  tool_call_id?: string;
  tool_name?: string;
  task_id: string;
  workspace_id?: string;
  actor_id?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface PolicyRule {
  name: string;
  description?: string;
  effect: string;
  priority?: number;
  conditions: Record<string, unknown>;
  transformations?: { type: string; target: string; value?: string }[];
  approval?: { scope: string; ttl_seconds: number; reason?: string };
}

export interface PolicyPack {
  name: string;
  version: string;
  description?: string;
  rules: PolicyRule[];
  domain_allowlist?: string[];
  domain_blocklist?: string[];
  default_effect?: string;
}

export interface SecurityEvent {
  event_id: string;
  event_type: string;
  tool_call_id: string;
  tool_name: string;
  task_id: string;
  workspace_id: string;
  actor_id: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

export interface SecurityStats {
  total: number;
  high: number;
  medium: number;
  low: number;
  unique_patterns: number;
}

export interface SecurityResponse {
  events: SecurityEvent[];
  total: number;
  limit: number;
  offset: number;
  stats: SecurityStats;
}

export interface WorkspaceMember {
  id: string;
  user_id: string;
  role: string;
  joined_at: string;
  email: string;
  display_name: string;
}

export interface MembersResponse {
  members: WorkspaceMember[];
  viewer_role: string;
}

// --- Phase 2: Dashboard Stats, Trust, Anomaly, Replay ---

export interface DashboardStatsMetrics {
  requests_per_minute: number;
  blocked_24h: number;
  avg_latency_ms: number;
  active_agents: number;
  pending_approvals: number;
  budget_burn_percent: number;
}

export interface ShieldScoreBreakdown {
  allowed_percent: number;
  transformed_percent: number;
  approval_percent: number;
  blocked_percent: number;
  allowed_count?: number;
  transformed_count?: number;
  approval_count?: number;
  blocked_count?: number;
}

export interface ShieldScore {
  score: number;
  total?: number;
  breakdown: ShieldScoreBreakdown;
}

export interface PipelineStageThroughput {
  stage: string;
  passed: number;
  failed: number;
}

export interface DashboardStatsResponse {
  metrics: DashboardStatsMetrics;
  shield_score: ShieldScore;
  pipeline_throughput: PipelineStageThroughput[];
}

export interface AnomalyAlert {
  alert_id: string;
  timestamp: string;
  anomaly_type: string;
  entity_type: 'actor' | 'tool' | 'workspace';
  entity_id: string;
  metric: string;
  current_value: number;
  baseline_mean: number;
  z_score: number;
  severity: 'low' | 'medium' | 'high';
}

export interface AnomalyBaselineReport {
  current: Record<string, number>;
  baseline: Record<string, number>;
  alerts: AnomalyAlert[];
}

export interface TrustScoreBreakdown {
  anomalies: { count: number; high_count: number; impact: number };
  dlp: { detections: number; high_severity: number; impact: number };
  error_rate: { pct: number; impact: number };
  escalations: { count: number; impact: number };
  budget_velocity: { current_rate: number; impact: number };
}

export interface TrustScoreResult {
  actor_id: string;
  score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  breakdown: TrustScoreBreakdown;
  calculated_at: string;
}

export interface ReplayComparison {
  tool_call_id: string;
  tool_name: string;
  original_decision: string;
  original_rule_id: string;
  replay_decision: string;
  replay_rule_id: string;
  changed: boolean;
  replay_reasons: string[];
}

export interface ReplayResult {
  task_id: string;
  policy_pack_path: string;
  total_calls: number;
  changed_count: number;
  comparisons: ReplayComparison[];
}

// --- LLM Monitoring ---

export interface LLMModelUsage {
  model: string;
  provider: string;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  avg_latency_ms: number;
}

export interface LLMUsageResponse {
  summary: {
    total_requests: number;
    total_cost_usd: number;
    total_tokens: number;
    avg_latency_ms: number;
  };
  by_model: LLMModelUsage[];
  time_series: Array<{ timestamp: string; requests: number; cost_usd: number; tokens: number }>;
}

// --- Billing Types ---

export interface BillingInfo {
  plan: string;
  limits: {
    calls_per_month: number;
    workspaces: number;
    members_per_workspace: number;
    api_keys_per_workspace: number;
    audit_retention_days: number;
    features: string[];
  };
  subscription: {
    id: string;
    status: string;
    stripe_customer_id?: string;
    current_period_start?: string;
    current_period_end?: string;
    cancel_at_period_end?: boolean;
  } | null;
}

export interface BillingUsage {
  plan: string;
  calls_this_month: number;
  calls_limit: number;
  allowed: boolean;
  usage_percent: number;
}

export interface BillingInvoice {
  id: string;
  number: string | null;
  status: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  hosted_invoice_url: string | null;
  created: string | null;
}

export interface ModelPricingEntry {
  input_per_token: number;
  output_per_token: number;
  source: 'built-in' | 'workspace';
}

export interface ModelPricingResponse {
  models: Record<string, ModelPricingEntry>;
  has_workspace_overrides: boolean;
}

export const api = {
  getMe: () => request<{ user: User; session?: { workspace_id?: string } }>('/auth/me'),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  login: (email: string, password: string) =>
    request<{ user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (email: string, password: string, display_name?: string) =>
    request<{ user: User }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, display_name }),
    }),
  getProfile: () => request<User>('/api/v1/user/profile'),
  updateProfile: (data: { display_name: string }) =>
    request<User>('/api/v1/user/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  completeOnboarding: () =>
    request<void>('/api/v1/user/onboarding', { method: 'PUT' }),
  getWorkspaces: () =>
    request<{ workspaces: Workspace[] }>('/api/v1/workspaces'),
  createWorkspace: (data: { name: string; slug?: string }) =>
    request<Workspace>('/api/v1/workspaces', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getWorkspace: (id: string) => request<Workspace>(`/api/v1/workspaces/${id}`),
  getWorkspaceStats: (id: string) =>
    request<WorkspaceStats>(`/api/v1/workspaces/${id}/stats`),
  getEvents: (wsId: string, query?: EventsQuery) => {
    const params = new URLSearchParams();
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== '') params.set(k, String(v));
      }
    }
    const qs = params.toString();
    return request<EventsResponse>(`/api/v1/workspaces/${wsId}/events${qs ? `?${qs}` : ''}`);
  },
  getApiKeys: (wsId: string) =>
    request<{ api_keys: ApiKey[] }>(`/api/v1/workspaces/${wsId}/api-keys`),
  createApiKey: (wsId: string, data: { name: string; roles?: string[]; tags?: string[] }) =>
    request<ApiKeyCreated>(`/api/v1/workspaces/${wsId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateApiKey: (wsId: string, keyId: string, data: { name?: string; tags?: string[] }) =>
    request<ApiKey>(`/api/v1/workspaces/${wsId}/api-keys/${keyId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  revokeApiKey: (wsId: string, keyId: string) =>
    request<void>(`/api/v1/workspaces/${wsId}/api-keys/${keyId}`, {
      method: 'DELETE',
    }),
  getSessions: (wsId: string, params?: { limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    return request<SessionsResponse>(
      `/api/v1/workspaces/${wsId}/sessions?${qs}`,
    );
  },
  getSessionDetail: (wsId: string, sessionId: string) =>
    request<SessionDetailResponse>(
      `/api/v1/workspaces/${wsId}/sessions/${sessionId}`,
    ),
  getTraces: (wsId: string, params?: { limit?: number; offset?: number; status?: string; tool?: string; event_type?: string }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    if (params?.status) qs.set('status', params.status);
    if (params?.tool) qs.set('tool', params.tool);
    if (params?.event_type) qs.set('event_type', params.event_type);
    return request<TracesResponse>(
      `/api/v1/workspaces/${wsId}/traces?${qs}`,
    );
  },
  deleteAllTraces: (wsId: string) =>
    request<{ status: string; message: string }>(`/api/v1/workspaces/${wsId}/traces`, {
      method: 'DELETE',
    }),
  deleteTrace: (wsId: string, taskId: string) =>
    request<{ status: string; message: string }>(`/api/v1/workspaces/${wsId}/traces/${taskId}`, {
      method: 'DELETE',
    }),
  deleteSession: (wsId: string, sessionId: string) =>
    request<{ status: string; message: string }>(`/api/v1/workspaces/${wsId}/sessions/${sessionId}`, {
      method: 'DELETE',
    }),
  getBudgets: (wsId: string) =>
    request<BudgetsResponse>(`/api/v1/workspaces/${wsId}/budgets`),
  getRateLimits: (wsId: string) =>
    request<RateLimitConfigResponse>(`/api/v1/workspaces/${wsId}/rate-limits`),
  updateRateLimits: (wsId: string, config: Partial<RateLimitConfig>) =>
    request<RateLimitConfigResponse>(`/api/v1/workspaces/${wsId}/rate-limits`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  deleteRateLimits: (wsId: string) =>
    request<RateLimitConfigResponse>(`/api/v1/workspaces/${wsId}/rate-limits`, {
      method: 'DELETE',
    }),
  getBudgetConfig: (wsId: string) =>
    request<BudgetConfigResponse>(`/api/v1/workspaces/${wsId}/budget-config`),
  updateBudgetConfig: (wsId: string, config: Partial<BudgetConfig>) =>
    request<BudgetConfigResponse>(`/api/v1/workspaces/${wsId}/budget-config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  deleteBudgetConfig: (wsId: string) =>
    request<BudgetConfigResponse>(`/api/v1/workspaces/${wsId}/budget-config`, {
      method: 'DELETE',
    }),
  getApprovals: (wsId: string) =>
    request<{ approvals: Approval[] }>(`/api/v1/workspaces/${wsId}/approvals`),
  approveApproval: (wsId: string, approvalId: string) =>
    request<{ status: string; approval_id: string }>(
      `/api/v1/workspaces/${wsId}/approvals/${approvalId}/approve`,
      { method: 'POST' },
    ),
  denyApproval: (wsId: string, approvalId: string, reason?: string) =>
    request<{ status: string; approval_id: string }>(
      `/api/v1/workspaces/${wsId}/approvals/${approvalId}/deny`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  getTraceDetail: (wsId: string, taskId: string) =>
    request<{ task_id: string; events: TraceDetailEvent[]; total: number }>(
      `/api/v1/workspaces/${wsId}/traces/${taskId}`,
    ),
  getPolicies: (wsId: string) =>
    request<{ policy: PolicyPack; is_custom: boolean }>(`/api/v1/workspaces/${wsId}/policies`),
  updatePolicy: (wsId: string, policy: PolicyPack) =>
    request<{ policy: PolicyPack; is_custom: boolean }>(`/api/v1/workspaces/${wsId}/policies`, {
      method: 'PUT',
      body: JSON.stringify(policy),
    }),
  deletePolicy: (wsId: string) =>
    request<{ status: string; policy: PolicyPack; is_custom: boolean }>(`/api/v1/workspaces/${wsId}/policies`, {
      method: 'DELETE',
    }),
  validatePolicy: (wsId: string, policy: PolicyPack) =>
    request<{ valid: boolean; errors: string[] }>(`/api/v1/workspaces/${wsId}/policies/validate`, {
      method: 'POST',
      body: JSON.stringify(policy),
    }),
  generateRule: (wsId: string, description: string) =>
    request<{ rule: PolicyRule; rules?: PolicyRule[] }>(`/api/v1/workspaces/${wsId}/policies/generate-rule`, {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),
  getSecurityEvents: (wsId: string, params?: { limit?: number; offset?: number; severity?: string; tool?: string; actor?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.tool) qs.set('tool', params.tool);
    if (params?.actor) qs.set('actor', params.actor);
    if (params?.q) qs.set('q', params.q);
    return request<SecurityResponse>(
      `/api/v1/workspaces/${wsId}/security?${qs}`,
    );
  },
  switchWorkspace: (wsId: string) =>
    request<Workspace & { role: string }>(`/api/v1/workspaces/${wsId}/switch`, {
      method: 'POST',
    }),
  getMembers: (wsId: string) =>
    request<MembersResponse>(`/api/v1/workspaces/${wsId}/members`),
  updateMember: (wsId: string, memberId: string, role: string) =>
    request<WorkspaceMember>(`/api/v1/workspaces/${wsId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),
  removeMember: (wsId: string, memberId: string) =>
    request<{ status: string; id: string }>(
      `/api/v1/workspaces/${wsId}/members/${memberId}`,
      { method: 'DELETE' },
    ),
  addMember: (wsId: string, email: string, role?: string) =>
    request<WorkspaceMember>(`/api/v1/workspaces/${wsId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),

  // --- Phase 2: Dashboard Stats, Trust, Anomaly, Replay ---

  getDashboardStats: (wsId: string) =>
    request<DashboardStatsResponse>(`/api/v1/workspaces/${wsId}/dashboard/stats`),

  getAnomalyBaseline: (wsId: string) =>
    request<AnomalyBaselineReport>(`/api/v1/workspaces/${wsId}/anomalies/baseline`),

  getAgentTrustScore: (wsId: string, actorId: string) =>
    request<TrustScoreResult>(`/api/v1/workspaces/${wsId}/agents/${actorId}/trust`),

  getTrustLeaderboard: (wsId: string) =>
    request<{ agents: TrustScoreResult[] }>(`/api/v1/workspaces/${wsId}/agents/trust-leaderboard`),

  replaySession: (wsId: string, taskId: string, policyPackPath: string) =>
    request<ReplayResult>(`/api/v1/workspaces/${wsId}/replay`, {
      method: 'POST',
      body: JSON.stringify({ task_id: taskId, policy_pack_path: policyPackPath }),
    }),

  getLLMUsage: (wsId: string, range?: string) =>
    request<LLMUsageResponse>(`/api/v1/workspaces/${wsId}/llm-usage?range=${range || '24h'}`),

  // --- Billing ---

  getBilling: (wsId: string) =>
    request<BillingInfo>(`/api/v1/workspaces/${wsId}/billing`),

  getBillingUsage: (wsId: string) =>
    request<BillingUsage>(`/api/v1/workspaces/${wsId}/billing/usage`),

  getBillingInvoices: (wsId: string) =>
    request<{ invoices: BillingInvoice[] }>(`/api/v1/workspaces/${wsId}/billing/invoices`),

  createCheckout: (wsId: string, plan: string) =>
    request<{ url: string }>(`/api/v1/workspaces/${wsId}/billing/checkout`, {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),

  createPortalSession: (wsId: string) =>
    request<{ url: string }>(`/api/v1/workspaces/${wsId}/billing/portal`, {
      method: 'POST',
    }),

  getModelPricing: (wsId: string) =>
    request<ModelPricingResponse>(`/api/v1/workspaces/${wsId}/model-pricing`),

  // --- Policy Builder Chat & Test ---

  policyChatStream: (wsId: string, messages: { role: string; content: string }[], currentPolicy?: PolicyPack): Promise<Response> =>
    fetch(`/api/v1/workspaces/${wsId}/policies/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, current_policy: currentPolicy }),
    }),

  evaluateTestPolicy: (wsId: string, policy: PolicyPack, toolCall: Record<string, unknown>) =>
    request<{ result: Record<string, unknown> }>(`/api/v1/workspaces/${wsId}/policies/evaluate-test`, {
      method: 'POST',
      body: JSON.stringify({ policy, tool_call: toolCall }),
    }),
};
